import { TRIGGER_OPERATORS, isUnaryOperator } from '../chunk-LGUPHS47.js';
import { unwrapData } from '../chunk-HIWBYL4O.js';
import '../chunk-HCZGW64N.js';
import { brandClass } from '../chunk-N7SZ66QO.js';
import '../chunk-PZ5AY32C.js';

// src/ml/registry.ts
var ML_MODEL_STATUSES = ["draft", "candidate", "shadow", "champion", "retired"];
var ML_RUNGS = ["global", "customer", "org", "tenant"];
var ML_WIDE_RUNGS = /* @__PURE__ */ new Set(["customer", "org"]);
var ML_RUNG_LABEL = {
  global: "System",
  customer: "Customer",
  org: "Organization",
  tenant: "Tenant"
};
var ML_RUNG_ORDER = ["tenant", "org", "customer", "global"];
var ML_ARTIFACT_FORMATS = [
  "authe.linear.v1",
  "authe.trees.v1",
  "authe.llm.bedrock.v1",
  "authe.http.v1"
];
var ML_ARTIFACT_FORMAT_LABEL = {
  "authe.linear.v1": "Linear / logistic model",
  "authe.trees.v1": "Gradient-boosted trees",
  "authe.llm.bedrock.v1": "Platform LLM prompt",
  "authe.http.v1": "Your HTTPS endpoint"
};
function defaultMlFamily(format) {
  return format === "authe.llm.bedrock.v1" ? "llm" : "numeric";
}
var ML_FORMAT_FAMILIES = {
  "authe.linear.v1": ["numeric"],
  "authe.trees.v1": ["numeric"],
  "authe.llm.bedrock.v1": ["llm"],
  "authe.http.v1": ["numeric", "llm"]
};
var ML_MAX_INLINE_ARTIFACT_BYTES = 256 * 1024;
var ML_REGISTER_STATUSES = ["draft", "candidate"];
var ML_BUCKET_KEYS = ["customer_id", "tx_id", "from_address", "org_id"];
var ML_BUCKET_KEY_LABEL = {
  customer_id: "Customer",
  tx_id: "Transaction",
  from_address: "Sender address",
  org_id: "Organization"
};
var ML_UPLOAD_FILES = {
  artifact: { artifact: "artifact.json" },
  dataset: { manifest: "manifest.json", rows: "rows.jsonl" }
};
function isTerminalTrainingRun(run) {
  return run.status === "succeeded" || run.status === "failed";
}
var SEGMENT = /^[A-Za-z0-9][A-Za-z0-9._+-]*$/;
var MAX_SEGMENT = 64;
function mlSegmentProblem(value, what = "Name") {
  const text = value.trim();
  if (!text) return `${what} is required.`;
  if (text.length > MAX_SEGMENT) return `${what} is at most ${MAX_SEGMENT} characters.`;
  if (!SEGMENT.test(text) || text.includes("..")) {
    return `${what} is a plain token: letters, digits, '.', '_', '+' and '-', starting with a letter or digit.`;
  }
  return null;
}
var ML_STATUS_TRANSITIONS = {
  draft: ["candidate", "shadow", "retired"],
  candidate: ["champion", "shadow", "retired"],
  shadow: ["candidate", "champion", "retired"],
  champion: ["retired"],
  retired: []
};
function mlTransitionTargets(status) {
  return ML_STATUS_TRANSITIONS[status] ?? [];
}
var ML_STATUS_LABEL = {
  draft: "Draft",
  candidate: "Candidate",
  shadow: "Shadow",
  champion: "Champion",
  retired: "Retired"
};
var ML_STATUS_MEANING = {
  draft: "Registered, never routed to.",
  candidate: "Eligible for an A/B slice of traffic and for promotion.",
  shadow: "Runs beside the champion on every call; its output is recorded, never used.",
  champion: "Answers every call the routing does not send to a candidate.",
  retired: "Kept for the record; never routed to again."
};
var ML_TRANSITION_VERB = {
  draft: "Back to draft",
  candidate: "Make candidate",
  shadow: "Run in shadow",
  champion: "Promote to champion",
  retired: "Retire"
};
function mlGateOf(version) {
  const gate = version.metrics?.gate;
  if (!gate || typeof gate !== "object" || typeof gate.passed !== "boolean") return null;
  const raw = gate;
  return {
    passed: raw.passed,
    reasons: Array.isArray(raw.reasons) ? raw.reasons.filter((r) => typeof r === "string") : [],
    checks: Array.isArray(raw.checks) ? raw.checks : []
  };
}
function mlPromotionNeedsForce(version) {
  if (version.status === "champion") return false;
  return mlGateOf(version)?.passed !== true;
}
function mlHeadlineMetrics(version) {
  const metrics = version.metrics ?? {};
  for (const split of ["test", "validation"]) {
    const value = metrics[split];
    if (value && typeof value === "object") return { split, metrics: value };
  }
  return null;
}
function mlRoutableVersions(versions) {
  return versions.filter((v) => v.status !== "retired" && v.status !== "champion");
}
function mlRoutingProblem(draft, versions) {
  const byVersion = new Map(versions.map((v) => [v.version, v]));
  const seen = /* @__PURE__ */ new Set();
  let total = 0;
  for (const route of draft.candidate_versions) {
    if (!route.version) return "Choose a version for every candidate slice.";
    if (!Number.isInteger(route.weight_pct) || route.weight_pct < 0 || route.weight_pct > 100) {
      return `${route.version}'s weight is a whole percentage from 0 to 100.`;
    }
    if (seen.has(route.version)) return `${route.version} is a candidate twice.`;
    seen.add(route.version);
    total += route.weight_pct;
  }
  if (total > 100) return `Candidate weights add up to ${total}%; at most 100% can be split.`;
  for (const version of draft.shadow_versions) {
    if (seen.has(version)) return `${version} is both a candidate and a shadow.`;
    seen.add(version);
  }
  for (const version of seen) {
    const artifact = byVersion.get(version);
    if (!artifact) return `${version} is not registered at this rung.`;
    if (artifact.status === "retired") return `${version} is retired and cannot be routed to.`;
    if (artifact.status === "champion") return `${version} is the champion; it already answers the remainder.`;
  }
  if (!ML_BUCKET_KEYS.includes(draft.bucket_key)) return "Choose what requests are bucketed by.";
  return null;
}
function mlChampionShare(draft) {
  const taken = draft.candidate_versions.reduce((sum, c) => sum + (Number.isFinite(c.weight_pct) ? c.weight_pct : 0), 0);
  return Math.max(0, 100 - taken);
}
var ML_DEFAULT_REASON_CODE = "ml.review_required";
var REASON_CODE = /^[a-z0-9][a-z0-9_.:-]{0,119}$/;
function mlReasonCodeProblem(code) {
  if (!code) return null;
  return REASON_CODE.test(code) ? null : "A reason code is short and lower-case: letters, digits, '_', '.', ':' and '-'.";
}
function mlOwnLiveModelNames(list) {
  const names = /* @__PURE__ */ new Set();
  for (const rung of list.rungs) {
    if (rung.scope === "global") continue;
    for (const model of rung.models) {
      const live = Object.entries(model.statuses).some(([status, count]) => status !== "retired" && (count ?? 0) > 0);
      if (live) names.add(model.name);
    }
  }
  return names;
}
function isMlRung(value) {
  return typeof value === "string" && ML_RUNGS.includes(value);
}

// src/ml/api.ts
var ML_API_ROOT = "/ml";
var seg = (value) => encodeURIComponent(value);
function scopeQuery(query) {
  return query?.scope ? { scope: query.scope } : void 0;
}
function createMlApi(client, options = {}) {
  const root = options.basePath ?? ML_API_ROOT;
  const models = `${root}/models`;
  const model = (name) => `${models}/${seg(name)}`;
  const read = async (path, opts = {}) => unwrapData(await client.get(path, opts));
  return {
    listModels: (query, signal) => read(models, { query: scopeQuery(query), signal }),
    getModel: (name, query, signal) => read(model(name), { query: scopeQuery(query), signal }),
    async registerVersion(name, input) {
      return unwrapData(await client.post(`${model(name)}/versions`, input));
    },
    async transition(name, version, input) {
      return unwrapData(
        await client.post(
          `${model(name)}/versions/${seg(version)}/transition`,
          input
        )
      );
    },
    getRouting: (name, query, signal) => read(`${model(name)}/routing`, { query: scopeQuery(query), signal }),
    async putRouting(name, input) {
      return (await unwrapData(await client.put(`${model(name)}/routing`, input))).routing;
    },
    async listHitlPolicies(name, query, signal) {
      return (await read(`${model(name)}/hitl`, {
        query: scopeQuery(query),
        signal
      })).hitl_policies;
    },
    async getHitlPolicy(name, policyId, query, signal) {
      return (await read(`${model(name)}/hitl/${seg(policyId)}`, {
        query: scopeQuery(query),
        signal
      })).hitl_policy;
    },
    async putHitlPolicy(name, policyId, input) {
      return unwrapData(
        await client.put(`${model(name)}/hitl/${seg(policyId)}`, input)
      ).hitl_policy;
    },
    async deleteHitlPolicy(name, policyId, query) {
      unwrapData(await client.delete(`${model(name)}/hitl/${seg(policyId)}`, { query: scopeQuery(query) }));
    },
    async getPrediction(query, signal) {
      const { model: name, features_hash, variant, version, scope } = query;
      const tx_id = query.tx_id?.trim() || void 0;
      return (await read(`${root}/predictions`, {
        query: { model: name, features_hash, variant, version, scope, tx_id },
        signal,
        // A memo is decided on later (approved / rejected); never serve a stale one.
        cacheTtlMs: 0
      })).prediction;
    },
    async createUpload(kind) {
      return unwrapData(await client.post(`${root}/uploads`, { kind })).upload;
    },
    async startTrainingRun(input) {
      return unwrapData(
        await client.post(`${root}/training-runs`, input, { retries: 0 })
      ).training_run;
    },
    async getTrainingRun(runId, signal) {
      return (await read(`${root}/training-runs/${seg(runId)}`, { signal, cacheTtlMs: 0 })).training_run;
    }
  };
}

// src/ml/md5.ts
var S = [
  7,
  12,
  17,
  22,
  7,
  12,
  17,
  22,
  7,
  12,
  17,
  22,
  7,
  12,
  17,
  22,
  5,
  9,
  14,
  20,
  5,
  9,
  14,
  20,
  5,
  9,
  14,
  20,
  5,
  9,
  14,
  20,
  4,
  11,
  16,
  23,
  4,
  11,
  16,
  23,
  4,
  11,
  16,
  23,
  4,
  11,
  16,
  23,
  6,
  10,
  15,
  21,
  6,
  10,
  15,
  21,
  6,
  10,
  15,
  21,
  6,
  10,
  15,
  21
];
var K = Array.from({ length: 64 }, (_, i) => Math.floor(Math.abs(Math.sin(i + 1)) * 4294967296) >>> 0);
var Md5 = class {
  static {
    brandClass(this, "ml/Md5");
  }
  a = 1732584193;
  b = 4023233417;
  c = 2562383102;
  d = 271733878;
  block = new Uint8Array(64);
  words = new Uint32Array(16);
  filled = 0;
  /** Total bytes hashed; a float is exact well past any upload's size. */
  length = 0;
  done = false;
  update(bytes) {
    if (this.done) throw new Error("Md5: update() after digest()");
    let offset = 0;
    this.length += bytes.length;
    while (offset < bytes.length) {
      const take = Math.min(64 - this.filled, bytes.length - offset);
      this.block.set(bytes.subarray(offset, offset + take), this.filled);
      this.filled += take;
      offset += take;
      if (this.filled === 64) {
        this.compress();
        this.filled = 0;
      }
    }
    return this;
  }
  /** The digest as 32 lower-case hex characters. Ends the hash. */
  hex() {
    if (this.done) throw new Error("Md5: hex() called twice");
    const bits = this.length * 8;
    const pad = new Uint8Array((this.filled < 56 ? 56 : 120) - this.filled);
    pad[0] = 128;
    this.update(pad);
    const tail = new Uint8Array(8);
    const low = bits >>> 0;
    const high = Math.floor(bits / 4294967296) >>> 0;
    for (let i = 0; i < 4; i += 1) {
      tail[i] = low >>> 8 * i & 255;
      tail[i + 4] = high >>> 8 * i & 255;
    }
    this.update(tail);
    this.done = true;
    let out = "";
    for (const word of [this.a, this.b, this.c, this.d]) {
      for (let i = 0; i < 4; i += 1) out += (word >>> 8 * i & 255).toString(16).padStart(2, "0");
    }
    return out;
  }
  compress() {
    const block = this.block;
    const m = this.words;
    for (let i = 0; i < 16; i += 1) {
      m[i] = block[i * 4] | block[i * 4 + 1] << 8 | block[i * 4 + 2] << 16 | block[i * 4 + 3] << 24;
    }
    let a = this.a;
    let b = this.b;
    let c = this.c;
    let d = this.d;
    for (let i = 0; i < 64; i += 1) {
      let f;
      let g;
      if (i < 16) {
        f = b & c | ~b & d;
        g = i;
      } else if (i < 32) {
        f = d & b | ~d & c;
        g = (5 * i + 1) % 16;
      } else if (i < 48) {
        f = b ^ c ^ d;
        g = (3 * i + 5) % 16;
      } else {
        f = c ^ (b | ~d);
        g = 7 * i % 16;
      }
      const next = d;
      d = c;
      c = b;
      const sum = a + f + K[i] + m[g] >>> 0;
      const s = S[i];
      b = b + (sum << s | sum >>> 32 - s) >>> 0;
      a = next;
    }
    this.a = this.a + a >>> 0;
    this.b = this.b + b >>> 0;
    this.c = this.c + c >>> 0;
    this.d = this.d + d >>> 0;
  }
};
var SLICE_BYTES = 4 * 1024 * 1024;
async function md5HexOfBlob(blob) {
  const hash = new Md5();
  for (let start = 0; start < blob.size; start += SLICE_BYTES) {
    hash.update(new Uint8Array(await blob.slice(start, start + SLICE_BYTES).arrayBuffer()));
  }
  return hash.hex();
}

// src/ml/upload.ts
var MlUploadError = class extends Error {
  constructor(reason, message, status) {
    super(message);
    this.reason = reason;
    this.status = status;
    this.name = "MlUploadError";
  }
  reason;
  status;
  static {
    brandClass(this, "ml/MlUploadError");
  }
};
function etagHex(header) {
  if (!header) return null;
  const text = header.trim().replace(/^W\//, "").replace(/^"|"$/g, "").toLowerCase();
  return text || null;
}
async function putPresignedMlFile(slot, body, options = {}) {
  const name = options.fileName ?? "the file";
  if (body.size > slot.max_bytes) {
    throw new MlUploadError("too_large", `${name} is ${body.size} bytes, above the ${slot.max_bytes}-byte limit.`);
  }
  const send = options.fetch ?? fetch;
  let response;
  try {
    response = await send(slot.upload_url, {
      method: slot.method ?? "PUT",
      // Signed INTO the URL: any other content type is refused by storage.
      headers: { "Content-Type": slot.content_type },
      body,
      signal: options.signal
    });
  } catch (error) {
    if (options.signal?.aborted) throw error;
    throw new MlUploadError("network", `Uploading ${name} failed before storage answered: ${String(error)}`);
  }
  if (!response.ok) {
    throw new MlUploadError("rejected", `Storage refused ${name} (HTTP ${response.status}); the upload may have expired.`, response.status);
  }
  const etag = etagHex(response.headers.get("ETag"));
  if (!etag) {
    throw new MlUploadError(
      "unconfirmed",
      `Storage accepted ${name} but returned no ETag to this page, so what it stored cannot be confirmed (the bucket's CORS must expose ETag to this origin).`
    );
  }
  const sent = await md5HexOfBlob(body);
  if (etag !== sent) {
    throw new MlUploadError("mismatch", `Storage reports different bytes for ${name} than were sent (ETag ${etag}, sent ${sent}).`);
  }
  return etag;
}
function slotOf(upload, file) {
  const slot = upload.files[file];
  if (!slot) throw new MlUploadError("missing_slot", `The upload ${upload.upload_id} has no slot for ${file}.`);
  return slot;
}
async function uploadMlArtifact(api, artifact, options = {}) {
  const upload = await api.createUpload("artifact");
  const file = ML_UPLOAD_FILES.artifact.artifact;
  await putPresignedMlFile(slotOf(upload, file), artifact, { ...options, fileName: file });
  return upload.upload_id;
}
async function uploadMlDataset(api, files, options = {}) {
  const upload = await api.createUpload("dataset");
  const { manifest, rows } = ML_UPLOAD_FILES.dataset;
  await putPresignedMlFile(slotOf(upload, manifest), files.manifest, { ...options, fileName: manifest });
  await putPresignedMlFile(slotOf(upload, rows), files.rows, { ...options, fileName: rows });
  return upload.upload_id;
}

// src/ml/hitl.ts
var ML_HITL_FIELDS = [
  { path: "prediction.score", label: "Score", kind: "number" },
  { path: "prediction.confidence", label: "Confidence", kind: "number" },
  { path: "prediction.label", label: "Label", kind: "string" },
  { path: "prediction.text", label: "LLM text", kind: "string" },
  { path: "prediction.failed", label: "Backend failed", kind: "boolean" },
  { path: "prediction.variant", label: "Routing variant", kind: "string" },
  { path: "prediction.model_version", label: "Model version", kind: "string" },
  { path: "prediction.tokens_in", label: "Tokens in", kind: "number" },
  { path: "prediction.tokens_out", label: "Tokens out", kind: "number" }
];
var NESTED_ROOTS = ["prediction.raw.", "prediction.metadata."];
var LOGICAL = /* @__PURE__ */ new Set(["and", "or", "not"]);
var MAX_DEPTH = 8;
var ML_HITL_EXAMPLE_CONDITION = {
  predicate: { field: "prediction.confidence", op: "<", value: 0.6 }
};
function isRecord(value) {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
function isMlHitlField(path) {
  if (ML_HITL_FIELDS.some((field) => field.path === path)) return true;
  return NESTED_ROOTS.some((root) => path.startsWith(root) && path.length > root.length && !path.endsWith("."));
}
function predicateProblem(raw, where) {
  if (!isRecord(raw)) return `${where}: a predicate is an object with field, op and value.`;
  const field = raw.field;
  if (typeof field !== "string" || !field) return `${where}: the predicate names no field.`;
  if (!isMlHitlField(field)) {
    return `${where}: "${field}" is not a prediction field. A review policy reads prediction.score, .confidence, .label, .text, .failed, .variant, .model_version, .tokens_in, .tokens_out, or prediction.raw.<path>.`;
  }
  const op = raw.op ?? "==";
  if (typeof op !== "string" || !TRIGGER_OPERATORS.includes(op)) {
    return `${where}: "${String(op)}" is not an operator (${TRIGGER_OPERATORS.join(", ")}).`;
  }
  if (!isUnaryOperator(op) && raw.value === void 0) {
    return `${where}: ${field} ${op} needs a value.`;
  }
  if ((op === "in" || op === "not_in") && !Array.isArray(raw.value)) {
    return `${where}: "${op}" compares against a list, e.g. ["a", "b"].`;
  }
  if (op === "matches" && typeof raw.value !== "string") {
    return `${where}: "matches" takes a regular expression as a string.`;
  }
  return null;
}
function conditionProblem(raw, where, depth) {
  if (depth > MAX_DEPTH) return `${where}: the condition is nested more than ${MAX_DEPTH} levels deep.`;
  if (!isRecord(raw)) return `${where}: a condition is a JSON object.`;
  if (raw.predicate !== void 0 && raw.predicate !== null) {
    if (raw.op !== void 0 && raw.op !== null) return `${where}: a condition is a predicate OR an operator over children, not both.`;
    return predicateProblem(raw.predicate, where);
  }
  const op = raw.op;
  if (op === void 0 || op === null) {
    return `${where}: give a predicate, or an operator ("and" / "or" / "not") over children.`;
  }
  if (typeof op !== "string" || !LOGICAL.has(op)) return `${where}: "${String(op)}" is not and / or / not.`;
  const children = raw.children;
  if (!Array.isArray(children) || children.length === 0) return `${where}: "${op}" needs at least one child condition.`;
  for (let i = 0; i < children.length; i += 1) {
    const problem = conditionProblem(children[i], `${where}.children[${i}]`, depth + 1);
    if (problem) return problem;
  }
  return null;
}
function parseMlHitlCondition(text) {
  if (!text.trim()) return { condition: null, problem: "Write the condition the policy fires on." };
  let parsed;
  try {
    parsed = JSON.parse(text);
  } catch (error) {
    return { condition: null, problem: `Not JSON: ${error instanceof Error ? error.message : String(error)}` };
  }
  const problem = conditionProblem(parsed, "condition", 0);
  return problem ? { condition: null, problem } : { condition: parsed, problem: null };
}
function mlHitlConditionSummary(condition) {
  if (!isRecord(condition)) return "";
  if (isRecord(condition.predicate)) {
    const { field, op = "==", value } = condition.predicate;
    const shown = op === "exists" ? "" : ` ${JSON.stringify(value)}`;
    return `${String(field)} ${String(op)}${shown}`;
  }
  const children = Array.isArray(condition.children) ? condition.children : [];
  const parts = children.map((child) => {
    const text = mlHitlConditionSummary(child);
    return isRecord(child) && !isRecord(child.predicate) ? `(${text})` : text;
  });
  if (condition.op === "not") return `not ${parts.join(" or ")}`;
  return parts.join(` ${String(condition.op ?? "and")} `);
}

// src/ml/training.ts
var DATASET_ID = /^[A-Za-z0-9][A-Za-z0-9._-]{0,127}$/;
var SHA256 = /^[0-9a-f]{64}$/;
function isRecord2(value) {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
function parseMlDatasetManifest(text) {
  let raw;
  try {
    raw = JSON.parse(text);
  } catch (error) {
    return { manifest: null, problem: `The manifest is not JSON: ${error instanceof Error ? error.message : String(error)}` };
  }
  if (!isRecord2(raw)) return { manifest: null, problem: "The manifest is not a JSON object." };
  if (raw.format !== "authe.dataset.v1") {
    return { manifest: null, problem: `The manifest's format is ${JSON.stringify(raw.format)}, not "authe.dataset.v1".` };
  }
  if (typeof raw.dataset_id !== "string" || !DATASET_ID.test(raw.dataset_id)) {
    return { manifest: null, problem: "The manifest's dataset_id is not a dataset id (letters, digits, '.', '_', '-')." };
  }
  if (typeof raw.feature_schema !== "string" || !raw.feature_schema) {
    return { manifest: null, problem: "The manifest names no feature_schema." };
  }
  if (!Array.isArray(raw.label_set) || raw.label_set.length === 0 || !raw.label_set.every((l) => typeof l === "string")) {
    return { manifest: null, problem: "The manifest's label_set is not a list of labels." };
  }
  if (typeof raw.jsonl_sha256 !== "string" || !SHA256.test(raw.jsonl_sha256)) {
    return { manifest: null, problem: "The manifest's jsonl_sha256 is not a sha256 hex digest." };
  }
  return { manifest: raw, problem: null };
}
function defaultMlTrainingConfig() {
  return {
    trainer: "logistic",
    class_weight: "balanced",
    logistic: { l2: 1, max_iter: 50, tol: 1e-8 },
    calibration: "platt",
    threshold: { objective: "max_f1" },
    gate: { min_auc: 0.8, max_ece: 0.1, min_test_examples: 50, min_positives: 10 }
  };
}
function parseMlTrainingConfig(text) {
  let raw;
  try {
    raw = JSON.parse(text);
  } catch (error) {
    return { config: null, problem: `Not JSON: ${error instanceof Error ? error.message : String(error)}` };
  }
  if (!isRecord2(raw)) return { config: null, problem: "The configuration is a JSON object." };
  if (raw.trainer !== void 0 && raw.trainer !== "logistic" && raw.trainer !== "boosted_trees") {
    return { config: null, problem: 'trainer is "logistic" or "boosted_trees".' };
  }
  return { config: raw, problem: null };
}
async function sha256HexOfBlob(blob) {
  const digest = await crypto.subtle.digest("SHA-256", await blob.arrayBuffer());
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, "0")).join("");
}
async function mlDatasetRowsProblem(manifest, rows) {
  const digest = await sha256HexOfBlob(rows);
  return digest === manifest.jsonl_sha256 ? null : `rows.jsonl's sha256 is ${digest.slice(0, 12)}\u2026, but the manifest pins ${manifest.jsonl_sha256.slice(0, 12)}\u2026: these are not the rows it describes.`;
}

// src/ml/display.ts
function modelLabel(model) {
  const name = model.modelName?.trim();
  if (!name) return null;
  const version = model.modelVersion?.trim();
  return version ? `${name} ${version}` : name;
}
function modelVariantLabel(variant) {
  const text = variant?.trim();
  return text ? text.replace(/-/, " ") : null;
}
function formatModelScore(score) {
  if (!Number.isFinite(score)) return "";
  if (score >= 0 && score <= 1) return `${Number((score * 100).toFixed(1))}%`;
  return String(Number(score.toFixed(4)));
}
function modelReasonText(code) {
  const words = code.replace(/[_:.-]+/g, " ").trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : "";
}

export { ML_API_ROOT, ML_ARTIFACT_FORMATS, ML_ARTIFACT_FORMAT_LABEL, ML_BUCKET_KEYS, ML_BUCKET_KEY_LABEL, ML_DEFAULT_REASON_CODE, ML_FORMAT_FAMILIES, ML_HITL_EXAMPLE_CONDITION, ML_HITL_FIELDS, ML_MAX_INLINE_ARTIFACT_BYTES, ML_MODEL_STATUSES, ML_REGISTER_STATUSES, ML_RUNGS, ML_RUNG_LABEL, ML_RUNG_ORDER, ML_STATUS_LABEL, ML_STATUS_MEANING, ML_STATUS_TRANSITIONS, ML_TRANSITION_VERB, ML_UPLOAD_FILES, ML_WIDE_RUNGS, Md5, MlUploadError, createMlApi, defaultMlFamily, defaultMlTrainingConfig, formatModelScore, isMlHitlField, isMlRung, isTerminalTrainingRun, md5HexOfBlob, mlChampionShare, mlDatasetRowsProblem, mlGateOf, mlHeadlineMetrics, mlHitlConditionSummary, mlOwnLiveModelNames, mlPromotionNeedsForce, mlReasonCodeProblem, mlRoutableVersions, mlRoutingProblem, mlSegmentProblem, mlTransitionTargets, modelLabel, modelReasonText, modelVariantLabel, parseMlDatasetManifest, parseMlHitlCondition, parseMlTrainingConfig, putPresignedMlFile, sha256HexOfBlob, uploadMlArtifact, uploadMlDataset };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map